import React, { useState } from 'react';
import { X, Smartphone, KeyRound } from 'lucide-react';

export default function LoginModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch('/api/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ mobile })
      });
      const data = await res.json();

      if (data.status) {
        setStep(2);
      } else {
        setError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error while sending OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ mobile, otp })
      });
      const data = await res.json();

      if (data.status) {
        // Save to localStorage
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_data', JSON.stringify(data.user));
        window.dispatchEvent(new Event('auth_changed'));
        if (onSuccess) onSuccess(data.user, data.token);
        onClose();
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error while verifying OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setMobile('');
    setOtp('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <div className="bg-white rounded-[2rem] w-full max-w-4xl shadow-2xl overflow-hidden relative animate-in fade-in zoom-in duration-300 flex flex-col md:flex-row">

        {/* Left Side - Image/Branding (Hidden on mobile) */}
        <div className="hidden md:flex md:w-5/12 relative bg-gradient-to-br from-blue-900 to-indigo-900 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80&w=800"
            alt="Concert background"
            className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-blue-900/40 backdrop-blur-[2px]"></div>

          <div className="relative z-10 p-10 flex flex-col justify-between h-full w-full">
            <img src="https://lokanatya.com/logo.png" alt="Lokanatya" className="h-12 w-auto self-start brightness-0 invert drop-shadow-md" />

            <div className="mb-4">
              <h2 className="text-xl md:text-3xl font-black text-white leading-tight mb-4 drop-shadow-lg">
                Experience the<br />Magic of Jatra
              </h2>
              <p className="text-blue-100 text-sm font-medium leading-relaxed drop-shadow">
                Sign in to manage your tickets, get exclusive offers, and enjoy a seamless booking experience.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-7/12 p-8 md:p-12 relative flex flex-col justify-center bg-white">

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 md:top-6 md:right-6 text-gray-400 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-full p-2.5 transition-colors"
          >
            <X size={20} />
          </button>

          <div className="w-full max-w-sm mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mb-6 shadow-sm border border-blue-100/50">
                {step === 1 ? <Smartphone size={24} /> : <KeyRound size={24} />}
              </div>
              <h2 className="text-xl md:text-3xl font-black text-gray-900 mb-2 tracking-tight">
                {step === 1 ? 'Welcome Back' : 'Verify OTP'}
              </h2>
              <p className="text-sm text-gray-500 font-medium">
                {step === 1
                  ? 'Enter your mobile number to securely sign in'
                  : `We sent a 6-digit code to +91 ${mobile}`
                }
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="w-full bg-red-50 text-red-600 text-sm font-bold p-4 rounded-xl mb-6 flex items-start border border-red-100">
                <span className="mr-2 text-lg leading-none">•</span> {error}
              </div>
            )}

            {/* Forms */}
            {step === 1 ? (
              <form onSubmit={handleSendOtp} className="w-full">
                <div className="mb-6 group">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                    Mobile Number
                  </label>
                  <div className="relative flex items-center bg-gray-50/50 border-2 border-gray-100 rounded-2xl focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-500/10 focus-within:border-blue-500 transition-all overflow-hidden">
                    <div className="pl-4 pr-3 py-4 flex items-center pointer-events-none bg-transparent">
                      <span className="text-gray-500 font-bold">+91</span>
                      <div className="h-5 w-px bg-gray-300 ml-3"></div>
                    </div>
                    <input
                      type="tel"
                      maxLength="10"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      className="w-full py-4 pr-4 bg-transparent text-gray-900 font-bold outline-none placeholder:text-gray-400"
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || mobile.length !== 10}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 md:py-3.5 rounded-xl font-bold text-base md:text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 hover:-translate-y-0.5 active:translate-y-0"
                >
                  {isLoading ? 'Sending...' : 'Continue'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="w-full">
                <div className="mb-8">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 ml-1 text-center md:text-left">
                    Security Code
                  </label>
                  <div className="relative flex justify-center gap-2 sm:gap-3 md:gap-4 w-full mx-auto md:mx-0">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`w-10 h-12 sm:w-11 sm:h-14 md:w-12 md:h-14 flex items-center justify-center text-xl sm:text-2xl font-black rounded-xl border-2 transition-all ${
                          otp.length === index
                            ? 'border-blue-500 bg-white ring-4 ring-blue-500/10 shadow-sm'
                            : otp[index]
                            ? 'border-blue-400 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        {otp[index] ? (
                          otp[index]
                        ) : otp.length === index ? (
                          <div className="w-0.5 h-6 bg-blue-500 animate-pulse rounded-full"></div>
                        ) : (
                          ''
                        )}
                      </div>
                    ))}
                    <input
                      type="tel"
                      maxLength="6"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-text z-10"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 md:py-3.5 rounded-xl font-bold text-base md:text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 hover:-translate-y-0.5 active:translate-y-0"
                >
                  {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                </button>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-sm text-gray-500 font-bold hover:text-blue-600 transition-colors"
                  >
                    Use a different number
                  </button>
                </div>
              </form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
